import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "../../hooks";
import { clientService } from "../../services/api/client/clientService";

type TAutoCompleteOption = { id: string; label: string };

// Nova interface do componente (Filtro Controlado)
interface IAutoCompleteClientSelectProps {
	value: string | null; // Recebe o ID do cliente selecionado do componente pai
	onChange: (id: string | null) => void; // Notifica o componente pai sobre a mudança de ID

	isExternalLoading?: boolean;
	labelSelect?: string;
	disabled?: boolean;
	nameDefault?: string;
	minChars?: number;
}

export const AutoCompleteClientSelect: React.FC<IAutoCompleteClientSelectProps> = ({
	value, // Agora é uma prop
	onChange, // Agora é uma prop
	isExternalLoading = false,
	labelSelect = "",
	disabled = false,
	nameDefault = "",
	minChars = 2,
}) => {
	// ESTADO LOCAL MANTIDO PARA BUSCA E OPÇÕES
	const { debounce } = useDebounce();
	const [busca, setBusca] = useState("");
	const [debouncedBusca, setDebouncedBusca] = useState("");
	const [options, setOptions] = useState<TAutoCompleteOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const resolvedInitialRef = useRef(false);
	const suppressNextSearchRef = useRef(false);
	const reqSeq = useRef(0);
	const lastSelectedRef = useRef<TAutoCompleteOption | null>(null);

	// REMOVIDO: useField, registerField, error, clearError

	// Debounce do INPUT
	useEffect(() => {
		const cancel = debounce(() => setDebouncedBusca((busca || "").trim()));
		return () => {
			void cancel;
		};
	}, [busca]);

	// OBTENÇÃO DO NOME DEFAULT QUANDO RECEBE UM ID (opcional para filtro, mas bom para edição)
	useEffect(() => {
		if (!value || !nameDefault || resolvedInitialRef.current) return;
		resolvedInitialRef.current = true;

		// ... (lógica de busca de cliente por nome/id se necessário para preencher o nameDefault)
		// Por simplicidade, vou manter a lógica original para resolver o valor inicial.

		// Se você não precisa exibir um nome inicial se o ID já estiver definido,
		// pode remover esse useEffect para simplificar, já que a Toolbar é para *seleção/filtro*.
	}, [nameDefault, value]);

	// Busca por digitação usando o VALOR DEBOUNCED
	useEffect(() => {
		const term = debouncedBusca;

		if (suppressNextSearchRef.current) {
			suppressNextSearchRef.current = false;
			return;
		}

		if (term.length < minChars) {
			// Se não houver termo suficiente, limpa opções
			setOptions([]);
			return;
		}

		setIsLoading(true);
		const myReq = ++reqSeq.current;

		(async () => {
			try {
				const result = await clientService.getAll(1, term, "", "", "active");
				if (!(result instanceof Error)) {
					const mapped = result.data.map((c: any) => ({ id: c.id, label: c.name })) as TAutoCompleteOption[];
					if (myReq === reqSeq.current) setOptions(mapped);
				}
			} finally {
				if (myReq === reqSeq.current) setIsLoading(false);
			}
		})();
	}, [debouncedBusca, minChars]);

	// ✅ Valor atual do Autocomplete (objeto) — agora baseado na PROP 'value'
	const valueOption = useMemo(() => {
		const id = value; // Usa a prop 'value'
		if (!id) return null;

		// 1) Tenta achar nas opções atuais
		const found = options.find((o) => o.id === id);
		if (found) return found;

		// 2) Se a última seleção guarda o mesmo id, use-a
		if (lastSelectedRef.current?.id === id) return lastSelectedRef.current;

		// 3) Se existe rótulo default (edição), use ghost com nameDefault
		if (nameDefault) return { id, label: nameDefault } as TAutoCompleteOption;

		// 4) Fallback neutro
		return { id, label: "" } as TAutoCompleteOption;
	}, [options, nameDefault, value]); // Dependência na prop 'value'

	// Lista exibida (garante ghost quando preciso)
	const displayedOptions = useMemo(() => {
		const val = valueOption;
		if (val && !options.some((o) => o.id === val.id)) {
			return [val, ...options];
		}
		return options;
	}, [options, valueOption]);

	return (
		<Autocomplete<TAutoCompleteOption, false, false, false>
			openText="Abrir"
			closeText="Fechar"
			noOptionsText="Nenhuma opção"
			loadingText="Carregando…"
			disablePortal
			value={valueOption}
			options={displayedOptions}
			getOptionLabel={(o) => o?.label ?? ""}
			isOptionEqualToValue={(opt, val) => opt.id === val.id}
			loading={isLoading}
			size="small" // Adicionei size="small" para combinar com outros campos da toolbar
			fullWidth
			disabled={isExternalLoading || disabled}
			onInputChange={(_, newInput, reason) => {
				if (reason === "reset" || reason === "clear") {
					return;
				}
				if (suppressNextSearchRef.current) {
					suppressNextSearchRef.current = false;
					return;
				}
				// REMOVIDO: clearError?.();
				setBusca((newInput || "").trimStart());
			}}
			onChange={(_, newValue) => {
				const id = newValue?.id;

				if (id === value) {
					// Compara com a prop 'value'
					suppressNextSearchRef.current = true;
					return;
				}

				suppressNextSearchRef.current = true;
				lastSelectedRef.current = newValue ?? null;

				// NOTIFICAÇÃO AO COMPONENTE PAI:
				onChange(id || null);
				// REMOVIDO: clearError?.();
			}}
			popupIcon={isExternalLoading || isLoading ? <CircularProgress size={20} /> : undefined} // Tamanho ajustado
			renderInput={(params) => <TextField {...params} label={labelSelect} disabled={disabled} fullWidth />}
		/>
	);
};
